import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from 'recharts';
import { fmt$ } from './utils';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const METRIC_INFO = {
  Return: "Raw % return over the backtest, capped at 100. Not risk-adjusted — that's what Sharpe is for.",
  Sharpe: 'Return per unit of volatility. Scaled so a Sharpe of 3 (excellent) hits 100; a Sharpe of 1 shows as ~33.',
  'Win Rate': 'Share of trades that closed profitably. Not weighted by size — a low win rate can still be a great strategy if winners are much bigger than losers.',
  'Risk Mgmt': 'Inverted max drawdown. Never lost more than 10% peak-to-trough → scores 90. Drew down 60% → scores 40.',
  Consistency: '% of calendar months that closed flat-or-up vs. the prior month, from the equity curve. Answers: steady grind, or one spike carrying the result?',
  Recovery: 'Net profit ÷ dollar size of the worst drawdown. A recovery factor of 5 (profit is 5x the worst drawdown) maxes this score.',
};

function InfoTip({ text }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!show) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [show]);

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block', marginLeft: 5 }}>
      <button
        onClick={() => setShow((s) => !s)}
        aria-label="More info"
        style={{
          width: 14, height: 14, borderRadius: '50%', border: '1px solid var(--muted)',
          background: 'none', color: 'var(--muted)', fontSize: 9, lineHeight: '12px',
          cursor: 'pointer', padding: 0, verticalAlign: 'middle',
        }}
      >
        ?
      </button>
      {show && (
        <div style={{
          position: 'absolute', zIndex: 20, top: '140%', left: 0,
          width: 220, padding: '8px 10px', borderRadius: 6,
          background: 'var(--card)', border: '1px solid var(--border)',
          color: 'var(--text)', fontSize: 11, lineHeight: 1.45,
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
        }}>
          {text}
        </div>
      )}
    </span>
  );
}

// Recharts' default Legend draws a plain color swatch and ignores strokeDasharray,
// so dashed reference lines (p5/p95) render identically to solid ones in the legend.
// Render our own swatches so the legend actually matches what's drawn on the chart.
const MONTE_CARLO_LINE_STYLE = {
  p95: { dasharray: '3 3', width: 1 },
  p50: { dasharray: undefined, width: 1.5 },
  p5: { dasharray: '3 3', width: 1 },
  actual: { dasharray: undefined, width: 2 },
};

function renderMonteCarloLegend({ payload }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', fontSize: 11, color: 'var(--muted)', paddingTop: 6 }}>
      {payload.map((entry) => {
        const style = MONTE_CARLO_LINE_STYLE[entry.dataKey] || {};
        return (
          <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="18" height="8">
              <line
                x1="0" y1="4" x2="18" y2="4"
                stroke={entry.color}
                strokeWidth={style.width || 1.5}
                strokeDasharray={style.dasharray}
              />
            </svg>
            <span>{entry.value}</span>
          </div>
        );
      })}
    </div>
  );
}

function computeStrategyProfile(results, equityCurve) {
  const returnPct = results.return_pct ?? 0;
  const sharpe = results.sharpe_ratio ?? 0;
  const won = results.won_trades ?? 0;
  const lost = results.lost_trades ?? 0;
  const totalTrades = won + lost;
  const winRate = totalTrades > 0 ? (won / totalTrades) * 100 : 0;
  const maxDD = Math.abs(results.max_drawdown_pct ?? 0);
  const netProfit = results.net_profit ?? 0;
  const startingValue = results.starting_value || 1;
  const ddDollar = (maxDD / 100) * startingValue;
  const recoveryFactor = ddDollar > 0 ? netProfit / ddDollar : (netProfit > 0 ? 5 : 0);

  // Consistency: fraction of calendar months that closed at or above the prior month's equity.
  let consistency = 50;
  if (equityCurve.length > 1) {
    const monthly = {};
    equityCurve.forEach(({ date, value }) => {
      monthly[String(date).slice(0, 7)] = value;
    });
    const values = Object.values(monthly);
    if (values.length > 1) {
      let positive = 0;
      for (let i = 1; i < values.length; i++) {
        if (values[i] >= values[i - 1]) positive++;
      }
      consistency = (positive / (values.length - 1)) * 100;
    }
  }

  return [
    { metric: 'Return', score: Math.round(clamp(returnPct, 0, 100)) },
    { metric: 'Sharpe', score: Math.round(clamp((sharpe / 3) * 100, 0, 100)) },
    { metric: 'Win Rate', score: Math.round(clamp(winRate, 0, 100)) },
    { metric: 'Risk Mgmt', score: Math.round(clamp(100 - maxDD, 0, 100)) },
    { metric: 'Consistency', score: Math.round(clamp(consistency, 0, 100)) },
    { metric: 'Recovery', score: Math.round(clamp(recoveryFactor * 20, 0, 100)) },
  ];
}

function computeRollingSharpe(equityCurve, window = 20) {
  if (equityCurve.length < window + 2) return [];
  const values = equityCurve.map((d) => d.value);
  const returns = [];
  for (let i = 1; i < values.length; i++) {
    returns.push(values[i - 1] !== 0 ? (values[i] - values[i - 1]) / values[i - 1] : 0);
  }
  const out = [];
  for (let i = window; i <= returns.length; i++) {
    const win = returns.slice(i - window, i);
    const mean = win.reduce((a, b) => a + b, 0) / window;
    const variance = win.reduce((a, b) => a + (b - mean) ** 2, 0) / window;
    const std = Math.sqrt(variance);
    const sharpe = std > 0 ? (mean / std) * Math.sqrt(252) : 0;
    out.push({ date: equityCurve[i]?.date, sharpe: Number(sharpe.toFixed(2)) });
  }
  return out;
}

function shuffledCopy(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function computeMonteCarlo(trades, startingValue, iterations = 200) {
  if (trades.length < 5) return [];
  const pctReturns = trades.map((t) => (t.pnl_pct ?? 0) / 100);
  const n = pctReturns.length;

  const paths = [];
  for (let it = 0; it < iterations; it++) {
    const shuffled = shuffledCopy(pctReturns);
    let equity = startingValue;
    const path = [equity];
    for (let i = 0; i < n; i++) {
      equity *= (1 + shuffled[i]);
      path.push(equity);
    }
    paths.push(path);
  }

  let actualEquity = startingValue;
  const actualPath = [actualEquity];
  pctReturns.forEach((r) => {
    actualEquity *= (1 + r);
    actualPath.push(actualEquity);
  });

  const out = [];
  for (let step = 0; step <= n; step++) {
    const stepValues = paths.map((p) => p[step]).sort((a, b) => a - b);
    const pick = (pctl) => stepValues[Math.min(stepValues.length - 1, Math.floor(pctl * (stepValues.length - 1)))];
    out.push({
      step,
      p5: Math.round(pick(0.05)),
      p50: Math.round(pick(0.5)),
      p95: Math.round(pick(0.95)),
      actual: Math.round(actualPath[step]),
    });
  }
  return out;
}

const DURATION_BUCKETS = [
  { label: '<1h', max: 1 },
  { label: '1-4h', max: 4 },
  { label: '4-12h', max: 12 },
  { label: '12-24h', max: 24 },
  { label: '1-3d', max: 72 },
  { label: '3d+', max: Infinity },
];

const PNL_BUCKETS = [
  { label: '+5%+', min: 5 },
  { label: '+1 to 5%', min: 1 },
  { label: '0 to 1%', min: 0 },
  { label: '-1 to 0%', min: -1 },
  { label: '-5 to -1%', min: -5 },
  { label: '<-5%', min: -Infinity },
];

function bucketDuration(hours) {
  if (hours == null) return DURATION_BUCKETS[DURATION_BUCKETS.length - 1].label;
  const found = DURATION_BUCKETS.find((b) => hours <= b.max);
  return found ? found.label : DURATION_BUCKETS[DURATION_BUCKETS.length - 1].label;
}

function bucketPnl(pct) {
  if (pct == null) return PNL_BUCKETS[PNL_BUCKETS.length - 1].label;
  const found = PNL_BUCKETS.find((b) => pct >= b.min);
  return found ? found.label : PNL_BUCKETS[PNL_BUCKETS.length - 1].label;
}

function buildDurationHeatmap(trades) {
  const grid = {};
  PNL_BUCKETS.forEach((p) => {
    grid[p.label] = {};
    DURATION_BUCKETS.forEach((d) => { grid[p.label][d.label] = 0; });
  });
  trades.forEach((t) => {
    grid[bucketPnl(t.pnl_pct)][bucketDuration(t.duration_hours)] += 1;
  });
  let maxCount = 1;
  Object.values(grid).forEach((row) => Object.values(row).forEach((c) => { if (c > maxCount) maxCount = c; }));
  return { grid, maxCount };
}

export default function AdvancedAnalyticsSection({ backtest, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const results = useMemo(() => backtest.results || {}, [backtest.results]);
  const equityCurve = useMemo(() => backtest.equity_curve || [], [backtest.equity_curve]);
  const trades = useMemo(() => results.closed_trades || [], [results]);

  const profile = useMemo(() => computeStrategyProfile(results, equityCurve), [results, equityCurve]);
  const rollingSharpe = useMemo(() => computeRollingSharpe(equityCurve), [equityCurve]);
  const monteCarlo = useMemo(
    () => computeMonteCarlo(trades, results.starting_value || 100000),
    [trades, results],
  );
  const { grid: heatmapGrid, maxCount: heatmapMax } = useMemo(() => buildDurationHeatmap(trades), [trades]);

  if (trades.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 8,
          marginTop: 28, marginBottom: open ? 4 : 0,
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{open ? '▼' : '▶'}</span>
        <h3 style={{ margin: 0, color: 'var(--text)' }}>Advanced Analytics</h3>
      </button>
      {!open && (
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
          Strategy profile, rolling Sharpe, Monte Carlo, PnL waterfall &amp; duration heatmap — click to expand.
        </div>
      )}
      {open && (
      <>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14 }}>
        Computed from this backtest's {trades.length} closed trade{trades.length !== 1 ? 's' : ''}.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div>
          <h4 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 14 }}>Strategy Profile</h4>
          <ResponsiveContainer width="100%" height={240}>
            <RadarChart data={profile} outerRadius="70%">
              <PolarGrid stroke="var(--border)" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: 'var(--muted)' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: 'var(--muted)' }} />
              <Radar dataKey="score" stroke="var(--teal)" fill="var(--teal)" fillOpacity={0.35} />
              <Tooltip
                formatter={(val) => [`${val}/100`, 'Score']}
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
              />
            </RadarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 14px', marginTop: 4 }}>
            {profile.map((p) => (
              <div key={p.metric} style={{ display: 'flex', alignItems: 'center', fontSize: 11, color: 'var(--muted)' }}>
                <span style={{ color: 'var(--text)', fontWeight: 600, marginRight: 4 }}>{p.metric}:</span>
                {p.score}
                <InfoTip text={METRIC_INFO[p.metric]} />
              </div>
            ))}
          </div>
        </div>

        {rollingSharpe.length > 0 && (
          <div>
            <h4 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 14 }}>Rolling Sharpe Ratio (20-bar)</h4>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={rollingSharpe} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} domain={['auto', 'auto']} />
                <Tooltip
                  formatter={(val) => [val, 'Sharpe']}
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
                />
                <Line type="monotone" dataKey="sharpe" stroke="var(--teal)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {monteCarlo.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ margin: '0 0 4px', color: 'var(--text)', fontSize: 14 }}>Monte Carlo — Trade Order Sensitivity</h4>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
            The same {trades.length} trades replayed in 200 random orders. A tight band means the result doesn't depend on luck of sequencing.
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={monteCarlo} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="step" tick={{ fontSize: 10, fill: 'var(--muted)' }} label={{ value: 'Trade #', position: 'insideBottom', offset: -2, fontSize: 10, fill: 'var(--muted)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} domain={['auto', 'auto']} tickFormatter={(v) => '$' + (v / 1000).toFixed(0) + 'k'} />
              <Tooltip
                formatter={(val, name) => ['$' + Number(val).toLocaleString(), name]}
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
              />
              <Legend content={renderMonteCarloLegend} />
              <Line type="monotone" dataKey="p95" stroke="var(--muted)" strokeDasharray="3 3" dot={false} name="95th pct" strokeWidth={1} />
              <Line type="monotone" dataKey="p50" stroke="var(--muted)" dot={false} name="Median (shuffled)" strokeWidth={1.5} />
              <Line type="monotone" dataKey="p5" stroke="var(--muted)" strokeDasharray="3 3" dot={false} name="5th pct" strokeWidth={1} />
              <Line type="monotone" dataKey="actual" stroke="var(--teal)" dot={false} name="Actual order" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 14 }}>Trade PnL Waterfall</h4>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={trades.map((t, i) => ({ idx: i + 1, pnl: t.pnl ?? 0 }))} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="idx" tick={{ fontSize: 10, fill: 'var(--muted)' }} label={{ value: 'Trade #', position: 'insideBottom', offset: -2, fontSize: 10, fill: 'var(--muted)' }} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} tickFormatter={(v) => fmt$(v)} />
            <Tooltip
              formatter={(val) => [fmt$(val), 'PnL']}
              contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
            />
            <Bar dataKey="pnl" isAnimationActive={false}>
              {trades.map((t, i) => (
                <Cell key={i} fill={(t.pnl ?? 0) >= 0 ? '#26a641' : '#f85149'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div>
        <h4 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 14 }}>Trade Duration vs. PnL</h4>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
          Cross-tabulation of how long trades were held against how they performed. Brighter cells show where trades cluster.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
            <thead>
              <tr>
                <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}></th>
                {DURATION_BUCKETS.map((d) => (
                  <th key={d.label} style={{ padding: '6px 10px', textAlign: 'center', color: 'var(--text)', fontWeight: 600, fontSize: 11 }}>
                    {d.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PNL_BUCKETS.map((p) => (
                <tr key={p.label}>
                  <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--text)', fontSize: 11, whiteSpace: 'nowrap' }}>{p.label}</td>
                  {DURATION_BUCKETS.map((d) => {
                    const count = heatmapGrid[p.label][d.label];
                    const intensity = count / heatmapMax;
                    const hue = p.min >= 0 ? '142' : '0';
                    const bg = count > 0 ? `hsla(${hue}, 70%, 50%, ${clamp(intensity, 0.08, 0.85)})` : 'transparent';
                    return (
                      <td key={d.label} style={{
                        padding: '6px 10px',
                        textAlign: 'center',
                        background: bg,
                        color: 'var(--text)',
                        fontWeight: count > 0 ? 700 : 400,
                        borderRadius: 2,
                        border: '1px solid var(--border)',
                      }}>
                        {count}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </>
  );
}
